import _ from 'underscore';
import React, { useState, memo, useContext, useCallback } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import type {
  ScopeContextValue,
  ScopeValue,
} from '../types';

export const ScopeContext = React.createContext<ScopeContextValue>({
  scope: {},
  update: _.noop,
});

export const useScope = () => {
  const { scope } = useContext(ScopeContext);

  return scope;
};

export const withScopeProvider = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  const HoC = memo((props: P) => {
    const [scope, setScope] = useState<ScopeValue>({});

    // Currently two consumers rendered at the same time will kick off two separate requests
    const update = useCallback((newScope: ScopeValue) => {
      setScope(prevScope => ({
        ...prevScope,
        ...newScope,
      }));
    }, []);

    return (
      <ScopeContext.Provider value={{
        scope,
        update,
      }}
      >
        <WrappedComponent {...props} />
      </ScopeContext.Provider>);
  });

  HoC.displayName = `withScopeProvider(${getDisplayName(WrappedComponent)})`;

  return hoistNonReactStatics(HoC, WrappedComponent);
};
